import { memo, useEffect, useState } from 'react';
import clsx from 'clsx';

import styled from '@emotion/styled';
import { generateUtilityClasses, IconButton, IconButtonGhost } from '@kc-console/common-ui';
import { useTranslation } from '@kc-console/core';
import { IcRefreshRegular } from '@kep/kcds-icons';

import type {
  IconButtonGhostProps,
  IconButtonGhostVariant,
  IconButtonProps,
  IconButtonVariant,
} from '@kc-console/common-ui';

export type RefetchButtonOwnProps = {
  /**
   * 버튼의 모양을 설정합니다. 기본값은 'ghost'입니다.
   */
  appearance?: 'solid' | 'ghost';
  /**
   * Refetch가 진행되는 동안 버튼의 아이콘이 회전합니다.
   */
  isFetching?: boolean;
};

export type RefetchButtonProps = RefetchButtonOwnProps &
  (({ appearance: 'solid' } & IconButtonProps) | ({ appearance?: 'ghost' } & IconButtonGhostProps));

export const refetchButtonClasses = generateUtilityClasses('RefetchButton', [
  'root',
  'icon',
  'animating',
]);

export default memo(function RefetchButton(props: RefetchButtonProps) {
  const { t } = useTranslation();
  const {
    className,
    appearance = 'ghost',
    variant = 'primary',
    size = 'md',
    isFetching,
    onClick,
    'aria-label': ariaLabelProp,
    ...others
  } = props;
  const [animationCount, setAnimationCount] = useState(0);
  const isAnimating = animationCount > 0;

  useEffect(() => {
    if (isFetching) {
      setAnimationCount(prev => prev + 1);
    }
  }, [isFetching]);

  const handleAnimationEnd = () => {
    if (isFetching) {
      setAnimationCount(prev => prev + 1);
    } else {
      setAnimationCount(0);
    }
  };

  const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (isFetching) return;
    onClick?.(e);
  };

  const sharedProps =
    appearance === 'solid'
      ? { appearance, variant: variant as IconButtonVariant }
      : { appearance, variant: variant as IconButtonGhostVariant };

  return (
    <RefetchButtonRoot
      {...sharedProps}
      className={clsx(
        refetchButtonClasses.root,
        className,
        isAnimating && refetchButtonClasses.animating,
      )}
      size={size}
      aria-label={ariaLabelProp ?? t('ui:action.refresh')}
      onClick={handleClick}
      onAnimationEnd={handleAnimationEnd}
      {...others}
    >
      <IcRefreshRegular key={animationCount} className={refetchButtonClasses.icon} />
    </RefetchButtonRoot>
  );
});

const RefetchButtonRoot = styled(
  ({
    appearance = 'ghost',
    'aria-label': ariaLabel,
    ...others
  }:
    | ({ appearance: 'solid' } & IconButtonProps)
    | ({ appearance?: 'ghost' } & IconButtonGhostProps)) =>
    appearance === 'solid' ? (
      <IconButton {...others} aria-label={ariaLabel} />
    ) : (
      <IconButtonGhost {...(others as IconButtonGhostProps)} aria-label={ariaLabel} />
    ),
  {
    target: refetchButtonClasses.root,
  },
)`
  @keyframes rotate {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }

  &.${refetchButtonClasses.animating} .${refetchButtonClasses.icon} {
    animation: rotate 1s linear 1 reverse;
  }
`;
